import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sparkles, ArrowLeft, User, Briefcase, Mail, Phone, Globe, Linkedin, Twitter, Github, Instagram, Upload, AlertCircle, MessageCircle, Scan, Camera, Zap } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import UpgradeModal from '../components/UpgradeModal';
import SignInModal from '../components/SignInModal';
import SEO from '../components/SEO';
import { generateCanonicalUrl } from '../utils/seo';
import { getBreadcrumbSchema } from '../utils/structuredData';
import { compressImage, getBase64Size, isValidImageType } from '../utils/imageCompression';
import { validateAllUrls, getValidationError } from '../utils/urlValidation';
import { trackCardCreation, trackError } from '../utils/analytics';
import { traceCardCreation, traceImageCompression } from '../utils/performance';
import ImageCropDialog from '../components/ImageCropDialog';
import { extractSocialUsername } from '../utils/socialMediaExtractor';
import { uploadImage as firebaseUploadImage } from '../firebase/config';
import { COUNTRY_CODES, parsePhoneNumber, formatPhoneNumber, DEFAULT_COUNTRY_CODE } from '../utils/countryCodes';
import CountryCodeSelect from '../components/CountryCodeSelect';

const CreateCard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { limits, loading: limitsLoading, checkLimit, getLimitInfo } = useSubscriptionLimits();
  
  // Pre-fill user data if available
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    title: '',
    company: '',
    email: user?.email || '',
    phone: '',
    phoneCountryCode: DEFAULT_COUNTRY_CODE,
    websiteName: '',
    websiteTLD: '.com',
    location: '',
    locationAddress: '',
    bio: '',
    profileImage: user?.photoURL || '',
    companyLogo: '',
    qrColor: '#4F46E5', // Default indigo color
    // backgroundImage: '', // Removed - not needed
    linkedin: '',
    twitter: '',
    github: '',
    instagram: '',
    youtube: '',
    facebook: '',
    whatsapp: '',
    whatsappCountryCode: DEFAULT_COUNTRY_CODE
  });
  const [imagePreview, setImagePreview] = useState(user?.photoURL || '');
  const [logoPreview, setLogoPreview] = useState('');
  // const [backgroundPreview, setBackgroundPreview] = useState(''); // Removed - not needed
  const [validationErrors, setValidationErrors] = useState({});
  
  // Name availability check states
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameAvailable, setNameAvailable] = useState(true);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  
  // Crop dialog states
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropType, setCropType] = useState('profile'); // 'profile' or 'logo'
  
  // Scan card states
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanningCard, setScanningCard] = useState(false);
  const [frontCardImage, setFrontCardImage] = useState(null);
  const [backCardImage, setBackCardImage] = useState(null);
  const [frontCardPreview, setFrontCardPreview] = useState('');
  const [backCardPreview, setBackCardPreview] = useState('');
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Sign in modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  
  // NFC claim code (if user came from NFC scan)
  const [nfcClaimCode, setNfcClaimCode] = useState(null);

  // Update form when user data loads and check if user already has a card
  useEffect(() => {
    if (!user) {
      // Show sign in modal instead of error toast
      setShowSignInModal(true);
      return;
    }

    // Check for NFC claim code in localStorage
    const storedNfcCode = localStorage.getItem('nfc_claim_code');
    if (storedNfcCode) {
      setNfcClaimCode(storedNfcCode);
      console.log('📱 NFC claim code found:', storedNfcCode);
    }

    // Pre-fill user data from authentication
    setFormData(prev => ({
      ...prev,
      name: prev.name || user.displayName || '',
      email: prev.email || user.email || '',
      profileImage: prev.profileImage || user.photoURL || ''
    }));
    
    if (user.photoURL) {
      setImagePreview(user.photoURL);
    }
  }, [user, navigate, toast]);

  // Check subscription limits - redirect if limit reached
  useEffect(() => {
    if (!limitsLoading && limits && !limits.cards.canCreate) {
      // User has non-premium cards - show upgrade modal
      setShowUpgradeModal(true);
    }
  }, [limitsLoading, limits]);

  // Debounced name availability check
  useEffect(() => {
    if (!formData.name || formData.name.trim().length < 2) {
      setNameAvailable(true);
      setNameErrorMessage('');
      return;
    }

    const checkNameAvailability = async () => {
      setNameCheckLoading(true);
      try {
        const { cardAPI } = await import('../services/api');
        const result = await cardAPI.checkNameAvailability(formData.name.trim());
        setNameAvailable(result.available);
        setNameErrorMessage(result.available ? '' : result.message);
      } catch (error) {
        console.error('Error checking name availability:', error);
        // If check fails, allow submission (backend will validate)
        setNameAvailable(true);
        setNameErrorMessage('');
      } finally {
        setNameCheckLoading(false);
      }
    };

    const timeoutId = setTimeout(checkNameAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.name]);

  // Parse website URL to extract domain and TLD
  const parseWebsiteUrl = (url) => {
    if (!url) return { websiteName: '', websiteTLD: '.com' };
    
    try {
      let cleanUrl = url.trim();
      
      // Remove protocol (http://, https://)
      cleanUrl = cleanUrl.replace(/^https?:\/\//i, '');
      
      // Remove www.
      cleanUrl = cleanUrl.replace(/^www\./i, '');
      
      // Remove trailing slashes and paths
      cleanUrl = cleanUrl.split('/')[0];
      
      // Remove query parameters
      cleanUrl = cleanUrl.split('?')[0];
      
      // Split by dots to get domain parts
      const parts = cleanUrl.split('.');
      
      if (parts.length < 2) {
        // No TLD found, return as is
        return { websiteName: cleanUrl, websiteTLD: '.com' };
      }
      
      // Handle multi-part TLDs like .co.uk, .co.in
      let tld;
      let domain;
      
      if (parts.length >= 3 && ['co', 'com', 'ac', 'gov', 'org', 'net'].includes(parts[parts.length - 2])) {
        // Multi-part TLD (e.g., .co.uk, .co.in)
        tld = `.${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        domain = parts.slice(0, -2).join('.');
      } else {
        // Single-part TLD (e.g., .com, .in, .org)
        tld = `.${parts[parts.length - 1]}`;
        domain = parts.slice(0, -1).join('.');
      }
      
      return { websiteName: domain, websiteTLD: tld };
    } catch (error) {
      console.error('Error parsing website URL:', error);
      return { websiteName: url, websiteTLD: '.com' };
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Extract username from social media URLs
    const socialPlatforms = ['linkedin', 'twitter', 'instagram', 'github', 'youtube', 'facebook', 'whatsapp'];
    
    if (socialPlatforms.includes(name)) {
      const extractedValue = extractSocialUsername(name, value);
      setFormData(prev => ({ ...prev, [name]: extractedValue }));
    } else if (name === 'websiteName') {
      // Parse website URL to extract domain and TLD
      const parsed = parseWebsiteUrl(value);
      setFormData(prev => ({ 
        ...prev, 
        websiteName: parsed.websiteName,
        websiteTLD: parsed.websiteTLD
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isValidImageType(file)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF).",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 15MB.",
          variant: "destructive"
        });
        return;
      }

      // Read file and open crop dialog
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropType('profile');
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isValidImageType(file)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF).",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 15MB.",
          variant: "destructive"
        });
        return;
      }

      // Read file and open crop dialog
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropType('logo');
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedImageBlob) => {
    try {
      toast({
        title: "Uploading image...",
        description: "Please wait while we upload your high-quality image."
      });

      // Convert blob to file - keep original quality
      const file = new File([croppedImageBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      const originalSize = Math.round(file.size / 1024);

      // Upload directly to Firebase Storage without compression
      let imageUrl;
      try {
        if (user) {
          const storagePath = `${cropType === 'logo' ? 'company-logos' : 'profile-images'}/${user.uid}-${Date.now()}.jpg`;
          imageUrl = await firebaseUploadImage(file, storagePath);
        } else {
          toast({
            title: "Please sign in",
            description: "Sign in to upload images.",
            variant: "destructive"
          });
          return;
        }
      } catch (fbErr) {
        console.error('Firebase upload failed:', fbErr);
        toast({
          title: "Upload failed",
          description: "Failed to upload image to storage. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (cropType === 'profile') {
        setFormData(prev => ({ ...prev, profileImage: imageUrl }));
        setImagePreview(imageUrl);
      } else if (cropType === 'logo') {
        setFormData(prev => ({ ...prev, companyLogo: imageUrl }));
        setLogoPreview(imageUrl);
      }

      const typeNames = { profile: 'Photo', logo: 'Logo' };
      toast({
        title: `${typeNames[cropType]} uploaded successfully`,
        description: `High-quality image uploaded (${originalSize}KB)`
      });
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to process image. Please try another file.",
        variant: "destructive"
      });
    }
  };

  // Handle scan card image upload
  const handleScanImageUpload = (e, side) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isValidImageType(file)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF).",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 10MB for scan)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 10MB.",
          variant: "destructive"
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (side === 'front') {
          setFrontCardImage(file);
          setFrontCardPreview(reader.result);
        } else {
          setBackCardImage(file);
          setBackCardPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Process scanned card with OCR
  const handleScanCard = async () => {
    if (!frontCardImage) {
      toast({
        title: "Missing Card Image",
        description: "Please upload at least the front image of the card.",
        variant: "destructive"
      });
      return;
    }

    setScanningCard(true);
    try {
      const formDataToSend = new FormData();
      // The OCR endpoint expects 'file' parameter (only front image for now)
      formDataToSend.append('file', frontCardImage);

      const { scannedContactAPI } = await import('../services/api');
      const result = await scannedContactAPI.processOCR(formDataToSend);

      // Check if OCR was successful
      if (!result.success) {
        throw new Error(result.error || "Failed to extract data from card");
      }

      // Prefill form with extracted data
      if (result.data) {
        const extracted = result.data;
        setFormData(prev => ({
          ...prev,
          name: extracted.name || prev.name,
          title: extracted.title || extracted.designation || prev.title,
          company: extracted.company || prev.company,
          email: extracted.email || prev.email,
          phone: extracted.phone || prev.phone,
          websiteName: extracted.website ? extracted.website.split('.')[0] : prev.websiteName,
          websiteTLD: extracted.website ? '.' + extracted.website.split('.').slice(1).join('.') : prev.websiteTLD,
          location: extracted.location || prev.location,
          locationAddress: extracted.location_address || extracted.address || prev.locationAddress,
          linkedin: extracted.linkedin || prev.linkedin,
          twitter: extracted.twitter || prev.twitter,
          github: extracted.github || prev.github,
          instagram: extracted.instagram || prev.instagram,
          facebook: extracted.facebook || prev.facebook,
          whatsapp: extracted.whatsapp || prev.whatsapp,
        }));

        toast({
          title: "Card Scanned Successfully",
          description: "Form has been pre-filled with extracted data. Please review and edit as needed.",
        });

        setShowScanDialog(false);
        // Reset scan images
        setFrontCardImage(null);
        setBackCardImage(null);
        setFrontCardPreview('');
        setBackCardPreview('');
      }
    } catch (error) {
      console.error('Error scanning card:', error);
      
      // Extract error message safely
      let errorMessage = "Failed to scan card. Please try again or enter details manually.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle different error response formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          // Handle FastAPI validation errors (array of objects)
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Scan Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setScanningCard(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check subscription limit before allowing submission
    if (!checkLimit('cards')) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.title || !formData.company) {
      toast({
        title: "Missing Information",
        description: "Please fill in name, email, job title, and company.",
        variant: "destructive"
      });
      return;
    }

    // WhatsApp validation - now we have country code separately, just check number length
    if (formData.whatsapp && formData.whatsapp.trim()) {
      const digitsOnly = formData.whatsapp.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 6) {
        toast({
          title: "Invalid WhatsApp Number",
          description: "Please enter a valid WhatsApp number",
          variant: "destructive"
        });
        return;
      }
    }

    // Check name availability
    if (!nameAvailable) {
      toast({
        title: "Card Name Not Available",
        description: nameErrorMessage || "Please choose a different card name.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if user is authenticated
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to create a card.",
          variant: "destructive"
        });
        navigate('/');
        return;
      }

      // Import API service
      const { cardAPI } = await import('../services/api');
      
      // Pre-fill name with user's display name if empty
      const cardDataWithUser = {
        ...formData,
        name: formData.name || user.displayName || formData.email.split('@')[0],
        userId: user.uid,
        phone: formData.phone ? formatPhoneNumber(formData.phoneCountryCode, formData.phone) : '',
        whatsapp: formData.whatsapp ? formatPhoneNumber(formData.whatsappCountryCode, formData.whatsapp) : ''
      };
      
      // Remove country code fields as they're now embedded in phone/whatsapp
      delete cardDataWithUser.phoneCountryCode;
      delete cardDataWithUser.whatsappCountryCode;
      
      // Create card with performance tracing
      const newCard = await traceCardCreation(
        () => cardAPI.create(cardDataWithUser),
        formData
      );
      
      // Invalidate cache to ensure card list updates immediately
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      
      // Track card creation
      trackCardCreation(formData);
      
      // Trigger update event for other components
      localStorage.setItem('card_updated', Date.now().toString());
      window.dispatchEvent(new Event('storage'));
      
      // Check if this is an NFC claim flow - read directly from localStorage to ensure we have the latest value
      const nfcCodeToLink = localStorage.getItem('nfc_claim_code');
      console.log('🔍 NFC code from localStorage:', nfcCodeToLink);
      console.log('🔍 New card data:', JSON.stringify(newCard, null, 2));
      
      if (nfcCodeToLink) {
        try {
          // Link the NFC code with the newly created card
          const token = await user.getIdToken();
          const cardId = newCard.id || newCard._id;
          const cardUsername = newCard.username;
          
          console.log('📱 Attempting to link NFC code:', nfcCodeToLink);
          console.log('📱 Card ID:', cardId);
          console.log('📱 Card Username:', cardUsername);
          
          if (!cardId || !cardUsername) {
            console.error('❌ Missing card ID or username:', { cardId, cardUsername });
            localStorage.removeItem('nfc_claim_code');
            navigate('/my-cards');
            return;
          }
          
          const linkResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/${nfcCodeToLink}/link`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              card_id: cardId,
              username: cardUsername
            })
          });
          
          const linkData = await linkResponse.json();
          console.log('📱 NFC link response status:', linkResponse.status);
          console.log('📱 NFC link response data:', linkData);
          
          if (linkResponse.ok) {
            console.log('✅ NFC code linked successfully');
            // Clear the NFC code from localStorage
            localStorage.removeItem('nfc_claim_code');
            
            toast({
              title: "Card Created & NFC Linked!",
              description: "Your digital business card is now linked to your NFC card."
            });
            
            // Redirect to bio page
            navigate(`/bio/${cardUsername}`);
            return;
          } else {
            console.error('❌ Failed to link NFC code:', linkData);
            // Still clear the code to avoid loops
            localStorage.removeItem('nfc_claim_code');
          }
        } catch (nfcError) {
          console.error('❌ Error linking NFC code:', nfcError);
          localStorage.removeItem('nfc_claim_code');
        }
      }
      
      // Navigate to My Cards page (non-NFC flow)
      navigate('/my-cards');
      
      // Show success toast after navigation
      setTimeout(() => {
        toast({
          title: "Card Created!",
          description: `Your digital business card "${newCard.name}" has been created successfully.`
        });
      }, 100);
    } catch (error) {
      console.error('Error creating card:', error);
      
      // Track error
      trackError(error.response?.data?.detail || error.message, 'create_card');
      
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create card. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Generate structured data
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      getBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Create Card', path: '/create' }
      ])
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-50 to-pink-50 py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Create Your Digital Business Card"
        description="Create your professional digital business card with QR code in minutes. Add your contact info, social media links, and share instantly."
        keywords="create digital business card, make virtual business card, business card creator, online business card maker, free digital card"
        canonicalUrl={generateCanonicalUrl('/create')}
        structuredData={structuredData}
        noindex={false}
      />
      <div className="max-w-4xl mx-auto">
        {/* Modern Header */}
        <div className="mb-8 sm:mb-12">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-6 transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center mr-2 group-hover:shadow-md transition-shadow">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          
          {/* Title Section */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 via-amber-600 to-pink-600 shadow-xl mb-6 transform hover:scale-105 transition-transform">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Create Your <span className="bg-gradient-to-r from-amber-600 via-pink-600 to-amber-600 bg-clip-text text-transparent">Digital Card</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Build your professional digital business card in minutes with AI-powered OCR scanning
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 sm:space-y-6">
            {/* Free Plan Limit Warning Banner */}
            {!limitsLoading && !checkLimit('cards') && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Virtual Card Limit Reached
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                      You&apos;ve created {limits.cards.current} virtual card{limits.cards.current !== 1 ? 's' : ''}. Free plan includes only {limits.cards.max} card. 
                      Upgrade to Pro or Business plan to create unlimited cards.
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate('/pricing')}
                      className="bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-700 hover:to-pink-700 text-white font-semibold"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Now
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Basic Information */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="p-6 sm:p-8 bg-gradient-to-r from-amber-50 to-pink-50 border-b border-amber-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center text-xl sm:text-2xl font-bold text-gray-900">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center mr-3 shadow-lg">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    Basic Information
                  </CardTitle>
                  <Button
                    type="button"
                    onClick={() => setShowScanDialog(true)}
                    className="bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    size="sm"
                  >
                    <Scan className="w-4 h-4 mr-2" />
                    <span>Scan Card</span>
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-2 ml-13">Use the scan feature to auto-fill from your business card</p>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`mt-1 ${!nameAvailable ? 'border-red-500 focus:border-red-500' : ''}`}
                      />
                      {nameCheckLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                          <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    {!nameAvailable && nameErrorMessage && (
                      <p className="text-xs text-red-500 mt-1 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{nameErrorMessage}</span>
                      </p>
                    )}
                    {nameAvailable && formData.name.trim().length >= 2 && !nameCheckLoading && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ This name is available
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="title">Job Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="Senior Product Designer"
                      value={formData.title}
                      onChange={handleChange}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company">Company *</Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder="TechCorp Inc."
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    placeholder="Tell us about yourself..."
                    value={formData.bio}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="profileImage" className="text-sm font-semibold text-gray-700">Profile Photo</Label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">Your professional headshot or profile picture</p>
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    {(imagePreview || formData.profileImage) && (
                      <div className="flex-shrink-0">
                        <img 
                          src={imagePreview || formData.profileImage} 
                          alt="Preview"
                          className="w-24 h-24 rounded-2xl object-cover border-4 border-amber-100 shadow-lg"
                        />
                      </div>
                    )}
                    <div className="flex-1 w-full">
                      <Input
                        id="profileImageFile"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <label 
                        htmlFor="profileImageFile"
                        className="flex cursor-pointer group"
                      >
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-8 border-2 border-dashed border-amber-200 rounded-xl hover:border-amber-400 hover:bg-amber-50/50 transition-all">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Upload className="w-6 h-6 text-white" />
                          </div>
                          <div className="text-center">
                            <span className="text-sm font-medium text-gray-700">Upload Photo</span>
                            <p className="text-xs text-gray-500 mt-1">Max 15MB • JPG, PNG, WebP, or GIF</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="companyLogo" className="text-sm font-semibold text-gray-700">Company Logo <span className="text-gray-400 font-normal">(Optional)</span></Label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">This will appear inside your QR code</p>
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    {(logoPreview || formData.companyLogo) && (
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 rounded-2xl border-4 border-amber-100 bg-white p-3 shadow-lg flex items-center justify-center">
                          <img 
                            src={logoPreview || formData.companyLogo} 
                            alt="Logo Preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex-1 w-full">
                      <Input
                        id="companyLogoFile"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <label 
                        htmlFor="companyLogoFile"
                        className="flex cursor-pointer group"
                      >
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-8 border-2 border-dashed border-amber-200 rounded-xl hover:border-amber-400 hover:bg-amber-50/50 transition-all">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Upload className="w-6 h-6 text-white" />
                          </div>
                          <div className="text-center">
                            <span className="text-sm font-medium text-gray-700">Upload Logo</span>
                            <p className="text-xs text-gray-500 mt-1">Max 15MB • Square format recommended</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* QR Code Color */}
                <div>
                  <Label htmlFor="qrColor">QR Code Color</Label>
                  <p className="text-xs text-gray-500 mb-2">Choose the color for your QR code</p>
                  <div className="flex items-center gap-3">
                    <Input
                      id="qrColor"
                      type="color"
                      value={formData.qrColor}
                      onChange={(e) => setFormData({ ...formData, qrColor: e.target.value })}
                      className="w-20 h-10 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={formData.qrColor}
                      onChange={(e) => setFormData({ ...formData, qrColor: e.target.value })}
                      placeholder="#4F46E5"
                      className="flex-1"
                      maxLength={7}
                    />
                    <div 
                      className="w-10 h-10 rounded-lg border-2 border-gray-300"
                      style={{ backgroundColor: formData.qrColor }}
                    />
                  </div>
                </div>

                {/* Background Image Upload - Removed as per requirement */}
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="p-6 sm:p-8 bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100">
                <CardTitle className="flex items-center text-xl sm:text-2xl font-bold text-gray-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mr-3 shadow-lg">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  Contact Information
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2 ml-13">How people can reach you</p>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="email" className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">This email will be displayed on your business card</p>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      Phone
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <CountryCodeSelect
                        value={formData.phoneCountryCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, phoneCountryCode: e.target.value }))}
                        className="w-36"
                      />
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="9876543210"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value.replace(/[^\d\s\-()]/g, '') }))}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="websiteName" className="flex items-center">
                    <Globe className="w-4 h-4 mr-1" />
                    Website
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="websiteName"
                      name="websiteName"
                      placeholder="example"
                      value={formData.websiteName}
                      onChange={handleChange}
                      className="mt-1 flex-1"
                    />
                    <select
                      name="websiteTLD"
                      value={formData.websiteTLD}
                      onChange={handleChange}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value=".com">.com</option>
                      <option value=".in">.in</option>
                      <option value=".org">.org</option>
                      <option value=".net">.net</option>
                      <option value=".co">.co</option>
                      <option value=".io">.io</option>
                      <option value=".ai">.ai</option>
                      <option value=".dev">.dev</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Enter domain name without www or https</p>
                </div>

                <div>
                  <Label htmlFor="locationAddress" className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location Address
                  </Label>
                  <Input
                    id="locationAddress"
                    name="locationAddress"
                    placeholder="Enter address (e.g., 123 Main St, New York, NY)"
                    value={formData.locationAddress || ''}
                    onChange={(e) => {
                      const address = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        locationAddress: address,
                        location: address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : ''
                      }));
                    }}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your address - it will automatically create a Google Maps link
                    {formData.location && (
                      <a 
                        href={formData.location} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:text-blue-700 underline"
                      >
                        Preview on Google Maps
                      </a>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="p-6 sm:p-8 bg-gradient-to-r from-amber-50 to-amber-50 border-b border-amber-100">
                <CardTitle className="flex items-center text-xl sm:text-2xl font-bold text-gray-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-500 flex items-center justify-center mr-3 shadow-lg">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  Social Links
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2 ml-13">Connect your social media profiles</p>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="linkedin" className="flex items-center">
                      <Linkedin className="w-4 h-4 mr-1" />
                      LinkedIn
                    </Label>
                    <Input
                      id="linkedin"
                      name="linkedin"
                      placeholder="username or company/pipercard"
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Profile: username | Company: company/name</p>
                  </div>
                  <div>
                    <Label htmlFor="twitter" className="flex items-center">
                      <Twitter className="w-4 h-4 mr-1" />
                      Twitter
                    </Label>
                    <Input
                      id="twitter"
                      name="twitter"
                      placeholder="johndoe"
                      value={formData.twitter}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Handle without @</p>
                  </div>
                  <div>
                    <Label htmlFor="github" className="flex items-center">
                      <Github className="w-4 h-4 mr-1" />
                      GitHub
                    </Label>
                    <Input
                      id="github"
                      name="github"
                      placeholder="johndoe"
                      value={formData.github}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username only</p>
                  </div>
                  <div>
                    <Label htmlFor="instagram" className="flex items-center">
                      <Instagram className="w-4 h-4 mr-1" />
                      Instagram
                    </Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      placeholder="johndoe"
                      value={formData.instagram}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Handle without @</p>
                  </div>
                  <div>
                    <Label htmlFor="youtube" className="flex items-center">
                      <Globe className="w-4 h-4 mr-1" />
                      YouTube
                    </Label>
                    <Input
                      id="youtube"
                      name="youtube"
                      placeholder="@johndoe or channel ID"
                      value={formData.youtube}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Channel ID or @handle</p>
                  </div>
                  <div>
                    <Label htmlFor="facebook" className="flex items-center">
                      <Globe className="w-4 h-4 mr-1" />
                      Facebook
                    </Label>
                    <Input
                      id="facebook"
                      name="facebook"
                      placeholder="johndoe"
                      value={formData.facebook}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username only</p>
                  </div>
                  <div>
                    <Label htmlFor="whatsapp" className="flex items-center">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <CountryCodeSelect
                        value={formData.whatsappCountryCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatsappCountryCode: e.target.value }))}
                        className="w-36"
                      />
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        placeholder="9876543210"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value.replace(/[^\d\s\-()]/g, '') }))}
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs mt-1 text-gray-500">
                      Select country code and enter your WhatsApp number
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-center pt-8 pb-8">
              <Button 
                type="submit" 
                size="lg" 
                disabled={!nameAvailable || nameCheckLoading || !checkLimit('cards')}
                className="group relative w-full sm:w-auto bg-gradient-to-r from-amber-600 via-pink-600 to-amber-600 hover:from-amber-700 hover:via-pink-700 hover:to-amber-700 text-white px-12 py-6 text-lg font-semibold rounded-2xl shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                <span className="relative flex items-center justify-center">
                  <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                  {nameCheckLoading ? 'Checking availability...' : !checkLimit('cards') ? 'Upgrade to Create Card' : 'Create My Digital Card'}
                </span>
              </Button>
            </div>
          </div>
        </form>

        {/* Upgrade Modal */}
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={(open) => {
            setShowUpgradeModal(open);
            if (!open && !checkLimit('cards')) {
              // If they close modal and still can't create, redirect to profile
              navigate('/profile');
            }
          }}
          limitType="card"
          currentUsage={limits?.cards?.current || 0}
          maxLimit={limits?.cards?.max || 1}
          nonPremiumCount={limits?.cards?.nonPremiumCount || 0}
        />

        {/* Image Crop Dialog */}
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
          shape={cropType === 'profile' ? 'round' : 'rect'}
        />

        {/* Scan Card Dialog */}
        <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-amber-600" />
                Scan Physical Business Card
              </DialogTitle>
              <DialogDescription>
                Upload photos of your business card to automatically extract and prefill your information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Front Image Upload */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Front of Card *
                </Label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleScanImageUpload(e, 'front')}
                    className="hidden"
                    id="front-card-upload"
                  />
                  <label
                    htmlFor="front-card-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-500 transition-colors"
                  >
                    {frontCardPreview ? (
                      <img
                        src={frontCardPreview}
                        alt="Front of card"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <Upload className="w-12 h-12 mb-2" />
                        <p className="text-sm">Click to upload front image</p>
                        <p className="text-xs mt-1">JPEG, PNG, WebP (max 10MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Back Image Upload */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Back of Card (Optional)
                </Label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleScanImageUpload(e, 'back')}
                    className="hidden"
                    id="back-card-upload"
                  />
                  <label
                    htmlFor="back-card-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-500 transition-colors"
                  >
                    {backCardPreview ? (
                      <img
                        src={backCardPreview}
                        alt="Back of card"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <Upload className="w-12 h-12 mb-2" />
                        <p className="text-sm">Click to upload back image</p>
                        <p className="text-xs mt-1">JPEG, PNG, WebP (max 10MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowScanDialog(false);
                    setFrontCardImage(null);
                    setBackCardImage(null);
                    setFrontCardPreview('');
                    setBackCardPreview('');
                  }}
                  disabled={scanningCard}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleScanCard}
                  disabled={!frontCardImage || scanningCard}
                  className="bg-gradient-to-r from-amber-600 to-blue-500 hover:from-amber-700 hover:to-blue-600"
                >
                  {scanningCard ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" />
                      Scan & Prefill
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Sign In Modal */}
        <SignInModal 
          open={showSignInModal} 
          onOpenChange={setShowSignInModal}
          redirectPath="/create"
        />
      </div>
    </div>
  );
};

export default CreateCard;